/**
 * Cliente REST para el backend Postgres (deploy Oracle). Se activa cuando
 * VITE_BACKEND === 'api'. Convierte fechas ISO en objetos compatibles con
 * Firestore Timestamp (.toDate()/.toMillis()) para que las pantallas no cambien.
 */

const env: any = (import.meta as any).env ?? {};
export const USE_API: boolean = env.VITE_BACKEND === 'api';
export const API_BASE: string = env.VITE_API_URL || '/api';

const TOKEN_KEY = 'clinicat.token';

export function getToken(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  } catch {
    return null;
  }
}
export function setToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
}

// ---- Timestamp compatible con Firestore ----
export class Ts {
  _date: Date;
  constructor(d: Date) {
    this._date = d;
  }
  toDate() {
    return this._date;
  }
  toMillis() {
    return this._date.getTime();
  }
  static now() {
    return new Ts(new Date());
  }
  static fromDate(d: Date) {
    return new Ts(d);
  }
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}|$)/;

function reviveDates(value: any): any {
  if (value == null) return value;
  if (typeof value === 'string' && ISO_RE.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return new Ts(d);
    return value;
  }
  if (Array.isArray(value)) return value.map(reviveDates);
  if (typeof value === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(value)) out[k] = reviveDates(v);
    return out;
  }
  return value;
}

async function request(method: string, path: string, body?: any) {
  const token = getToken();
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    setToken(null);
    throw new Error('Sesión expirada. Volvé a entrar.');
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    // Los errores de negocio que el backend lanza con `throw new Error(...)` llegan
    // como { error: 'Internal Server Error', message: '<motivo real>' }. Preferimos
    // el motivo real (message) salvo que sea el genérico de Fastify.
    const generico = !data?.error || data.error === 'Internal Server Error' || data.error === 'Bad Request';
    throw new Error((generico && data?.message) || data?.error || `Error ${res.status}`);
  }
  return reviveDates(data);
}

export const api = {
  get: (path: string) => request('GET', path),
  post: (path: string, body?: any) => request('POST', path, body),
  patch: (path: string, body?: any) => request('PATCH', path, body),
  delete: (path: string) => request('DELETE', path),
};

// ---- Descriptor de query para useFirestoreCollection ----
export interface ApiQuery {
  __api: true;
  path: string;
  params?: Record<string, string | number | undefined | null>;
}

export function apiQuery(path: string, params?: ApiQuery['params']): ApiQuery {
  return { __api: true, path, params };
}

export function isApiQuery(q: any): q is ApiQuery {
  return q && typeof q === 'object' && q.__api === true;
}

export function buildUrl(q: ApiQuery): string {
  const qs = new URLSearchParams();
  if (q.params) {
    for (const [k, v] of Object.entries(q.params)) {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    }
  }
  const s = qs.toString();
  return s ? `${q.path}?${s}` : q.path;
}
